import classesK from '../kalkulatori.module.css'
import { useEffect, useState } from 'react'
import InfoBar from '../redesign/components/InfoBar'
import OddsCalculator from '../redesign/components/OddsCalculator'
import ResultsSection from '../redesign/components/ResultsSection'
import {
  footballSectionsData,
  getInitialData,
} from './FudbalSveIgreResultsData'
import { isValidFudbalSveIgreInput } from './FudbalSveIgreCalculator'
import runCalculator from '../api/runCalculator'

const calculatorType = 'Fudbal - sve igre'
const category = 'predictions'
const slug = 'football-all-markets'

// Build the API request body from the input state, matching the golden `input`
// shape (English keys). Odds are parsed as floats like the local code did.
const buildBody = ({ odds = [], totalGoals = [], bothTeams = [] }) => ({
  oddsHome: parseFloat(odds[0]),
  oddsDraw: parseFloat(odds[1]),
  oddsAway: parseFloat(odds[2]),
  oddsUnder25: parseFloat(totalGoals[0]),
  oddsOver25: parseFloat(totalGoals[1]),
  oddsBttsYes: parseFloat(bothTeams[0]),
  oddsBttsNo: parseFloat(bothTeams[1]),
})

// Map the API response (shaped like the golden `output`) into the exact
// `data.results` shape the JSX consumes: { [key]: { [subKey]: [...values] } }.
// Each response leaf is an object keyed by Serbian labels in display order, so
// its values (in order) become the array ResultsSection reads by index. A null
// value means an invalid cell, which the local logic rendered as '-'.
const mapResponseToResults = (output) => {
  const results = {}

  Object.entries(output || {}).forEach(([key, sections]) => {
    results[key] = {}

    Object.entries(sections || {}).forEach(([subKey, cells]) => {
      results[key][subKey] = Object.values(cells || {}).map((value) =>
        value === null || value === undefined ? '-' : value
      )
    })
  })

  return results
}

let FudbalSveIgre = () => {
  const [showInputArea, setShowInputArea] = useState(true)
  const [disabled, setDisabled] = useState(true)
  const [data, setData] = useState(getInitialData())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setDisabled(!isValidFudbalSveIgreInput(data.values))
  }, [data.values])

  const clearFields = () => {
    setData(getInitialData())
    setShowInputArea(true)
    setError('')
  }

  const handleCalculate = async () => {
    setError('')
    setLoading(true)

    try {
      const output = await runCalculator(category, slug, buildBody(data.values))
      const results = mapResponseToResults(output)

      setData((prev) => ({
        ...prev,
        results,
      }))
      return true
    } catch (err) {
      setError('Greška pri obračunu, pokušajte ponovo.')
      return false
    } finally {
      setLoading(false)
    }
  }

  const handleButtonClick = async () => {
    if (!showInputArea) {
      clearFields()
      return
    }

    const hasResults = await handleCalculate()

    if (hasResults) {
      setShowInputArea(false)
    }
  }

  return (
    <div className={classesK.container}>
      <InfoBar calculatorType={calculatorType} onRefresh={clearFields} />
      <div className={classesK.contentContainer}>
        <div className={classesK.submitWrapper}>
          <span>Unesi nove obračunske podatke</span>
          <button onClick={handleButtonClick} disabled={disabled || loading}>
            {loading
              ? 'Obračun u toku...'
              : showInputArea
              ? 'Unesi obračunske podatke'
              : 'Prikaži rezultate'}
          </button>
        </div>
        {error && <div className={classesK.errorMessage}>{error}</div>}
        {showInputArea && (
          <>
            <div className={classesK.sectionTitle}>
              Upiši kvote za ishod utakmice
            </div>
            <OddsCalculator
              data={data.values}
              setData={setData}
              field={'odds'}
              labelPrefix={[' KI 1', 'KI X', 'KI 2']}
              type={['number', 'number', 'number']}
              step={['0.1', '0.1', '0.1']}
              min={['0.1', '0.1', '0.1']}
              rows={1}
            />
            <div className={classesK.sectionTitle}>
              Upiši kvote za ukupno golova
            </div>
            <OddsCalculator
              data={data.values}
              setData={setData}
              field={'totalGoals'}
              labelPrefix={[
                'UG 0-2 ( od 0 do 2 gola )',
                'UG 3+ ( 3 ili više golova )',
              ]}
              type={['number', 'number']}
              step={['0.1', '0.1']}
              min={['0.1', '0.1']}
            />
            <div className={classesK.sectionTitle}>
              Upiši kvote za "Oba daju gol"
            </div>
            <OddsCalculator
              data={data.values}
              setData={setData}
              field={'bothTeams'}
              labelPrefix={[
                'GG ( oba tima daju gol na ut. )',
                'NEGG (min. jedan tim ne daje gol )',
              ]}
              type={['number', 'number']}
              step={['0.1', '0.1']}
              min={['0.1', '0.1']}
            />
          </>
        )}
        {!showInputArea && (
          <div className={classesK.sectionResults}>
            {footballSectionsData.map(({ title, key, sections }) =>
              sections.map(
                ({ subtitle, subKey, names, withPercent = true }, idx) => (
                  <ResultsSection
                    key={`${key}-${subKey}`}
                    title={idx === 0 ? title : undefined}
                    subtitle={subtitle}
                    names={names}
                    results={data.results?.[key]?.[subKey]}
                    withPercent={withPercent}
                    layoutPattern={'...-...+'}
                    allowNegative
                  />
                )
              )
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default FudbalSveIgre
